import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { PortalSessionResponse } from '@hector/shared-types';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/apiClient';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { useCabinetId } from '@/hooks/useCabinetMemberMe';
import { ErrorService } from '@/lib/errors/ErrorService';

/**
 * sessionStorage keys for the per-session dismissal of each dismissible
 * variant. Scoped by cabinetId so dismissing for cabinet A does not hide the
 * banner after a super-admin swaps to cabinet B in the same tab. Separate keys
 * per variant: dismissing the trial reminder must not hide the "free seats"
 * incentive that becomes eligible once the cabinet pays.
 */
const inviteDismissKey = (cabinetId: string) => `billing-invite-dismissed:${cabinetId}`;
const trialDismissKey = (cabinetId: string) => `billing-trial-dismissed:${cabinetId}`;

/**
 * Bandeau « non réglé » suspendu (2026-10-11, #2126) : le rattachement rétroactif
 * des abonnements Stripe aux cabinets est incomplet, donc des cabinets qui paient
 * sont lus comme non abonnés (`has_live_subscription` faux). Le bandeau leur
 * demandait de régler un abonnement déjà payé, et son bouton mène à un paiement
 * qui en ouvrirait un second. Tant que la base ne distingue pas ces cabinets d'un
 * vrai impayé, on ne l'affiche à personne. À repasser à `false` une fois le
 * rattachement réparé et contrôlé.
 */
const UNPAID_BANNER_SUSPENDED = true;

/**
 * Slim billing banner mounted once in DashboardLayout, above the page content.
 * The app stays fully usable underneath — this is an incentive, not a paywall.
 *
 * Decision matrix (derived from GET /api/stripe/status):
 *  - loading, fetch error (null), or `is_exempt` → renders nothing (fail-open,
 *    same philosophy as useManualPaywall).
 *  - `active` with `quantity > seats_used` (admin only, not canceling) →
 *    dismissible for the session. Incentive to invite a collaborator so the
 *    paid-for licences get used. CTA goes to Settings → Mes Collaborateurs.
 *  - `active` otherwise → renders nothing (healthy, fully used).
 *  - `trialing` on a REAL Stripe subscription (30-day trial of the « grand cabinet »
 *    signup path) → blue, dismissible: end date + link to Facturation. A trialing
 *    local placeholder is NOT a trial and falls to the amber variant below.
 *  - `past_due` → red, NOT dismissible. Admin CTA opens the Stripe Customer
 *    Portal deep-linked on the payment-method flow.
 *  - `paused` → neutral, NOT dismissible, no CTA: the pause is lifted by the
 *    Hector team in Stripe, there is nothing to settle from the app.
 *  - any other status ('none', 'trialing', 'canceled', 'unpaid', 'incomplete'…)
 *    → renders nothing while `UNPAID_BANNER_SUSPENDED` holds. Otherwise a neutral
 *    strip, NOT dismissible, whose admin CTA goes to Settings → Facturation
 *    where checkout lives.
 *  - billing managed by another space (`billing_manager`) → the status is the
 *    paying space's; no CTA, the text names who to contact; nothing when active.
 */
export function BillingBanner() {
  const navigate = useNavigate();
  const cabinetId = useCabinetId();
  const { data: status, isLoading } = useBillingStatus();
  const [portalLoading, setPortalLoading] = useState(false);
  // Holds the sessionStorage KEY dismissed during this mount (not a boolean,
  // not just the cabinetId): a plain boolean would leak the dismissal across a
  // super-admin cabinet swap AND across variants (unpaid → invite), and a lazy
  // useState initializer would run before cabinetId resolves (null at first
  // render). sessionStorage stays the durable per-session record.
  const [dismissedStorageKey, setDismissedStorageKey] = useState<string | null>(null);

  // Fail-open: nothing while loading, on error (status === null), or for
  // exempt members (staff).
  if (isLoading || !status || !cabinetId) return null;
  if (status.is_exempt) return null;

  // Reading sessionStorage at render time is deliberate: it is synchronous and
  // keeps the dismissal correct even when cabinetId resolves after mount.
  const isDismissed = (storageKey: string) =>
    dismissedStorageKey === storageKey || sessionStorage.getItem(storageKey) === 'true';

  const dismiss = (storageKey: string) => {
    sessionStorage.setItem(storageKey, 'true');
    setDismissedStorageKey(storageKey);
  };

  // Facturation gérée par un autre espace du cabinet (#1835) : l'admin d'ici n'a
  // aucune action à faire, seulement quelqu'un à prévenir. Pas d'incitation à
  // inviter non plus — le budget de licences est celui du gestionnaire.
  const manager = status.billing_manager;
  const managerContact = manager
    ? manager.email
      ? `Contactez ${manager.email}, qui gère votre abonnement.`
      : "Contactez l'administrateur de l'espace qui gère votre abonnement."
    : null;
  if (manager && status.billing_status === 'active') return null;

  if (status.billing_status === 'active') {
    const freeSeats = status.quantity - status.seats_used;
    // Invite incentive: only the admin can invite, and nudging a cabinet that
    // already scheduled its cancellation to fill seats would be absurd.
    if (!status.is_admin || freeSeats <= 0 || status.cancel_at_period_end) return null;
    if (isDismissed(inviteDismissKey(cabinetId))) return null;
    return (
      <BannerShell
        lead={freeSeats === 1 ? '1 licence disponible' : `${freeSeats} licences disponibles`}
        detail="dans votre abonnement. Invitez un collaborateur pour en profiter."
        action={{
          label: 'Inviter un collaborateur',
          // `openInvite` : Settings ouvre directement la fenêtre d'invitation au lieu
          // de laisser l'avocat chercher le bouton une fois sur l'onglet.
          onClick: () => navigate('/settings#collaborators', { state: { openInvite: Date.now() } }),
        }}
        onDismiss={() => dismiss(inviteDismissKey(cabinetId))}
      />
    );
  }

  // Essai de 30 jours sans carte (parcours « grand cabinet ») : un vrai abonnement
  // Stripe en `trialing`. Ce n'est PAS un abonnement « non réglé » — rappeler la
  // date de fin et proposer d'ajouter la carte, sans alarmer.
  if (status.billing_status === 'trialing' && status.has_live_subscription) {
    if (isDismissed(trialDismissKey(cabinetId))) return null;
    const trialEnd = status.trial_end ?? status.current_period_end;
    return (
      <BannerShell
        lead={`Essai gratuit${
          trialEnd
            ? ` jusqu'au ${new Date(trialEnd).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
            : ''
        }.`}
        detail={
          managerContact ??
          (status.is_admin
            ? 'Ajoutez un moyen de paiement pour continuer sans interruption.'
            : "L'administrateur de votre cabinet peut ajouter un moyen de paiement.")
        }
        action={
          status.is_admin && !manager
            ? { label: 'Voir ma facturation', onClick: () => navigate('/settings#facturation') }
            : undefined
        }
        onDismiss={() => dismiss(trialDismissKey(cabinetId))}
      />
    );
  }

  const isPastDue = status.billing_status === 'past_due';

  // Cabinet à plusieurs comptes de facturation : le bouton ouvre le Portal du
  // compte EN ÉCHEC, et seulement pour qui peut le gérer (admin ou membre désigné).
  const pastDueAccount = status.accounts.find((account) => account.billing_status === 'past_due');
  const canUpdatePayment = !manager && (pastDueAccount ? pastDueAccount.can_manage : status.is_admin);

  const handleUpdatePayment = async () => {
    setPortalLoading(true);
    try {
      const { url } = await apiClient.post<PortalSessionResponse>('/stripe/create-portal-session', {
        flow: 'payment_method_update',
        ...(pastDueAccount ? { subscription_id: pastDueAccount.subscription_id } : {}),
      });
      window.location.assign(url);
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'billing_banner_open_portal' }, silent: true });
      toast.error(
        "Impossible d'ouvrir la page de mise à jour du paiement. Réessayez dans quelques instants ou contactez le support (contact@hector.legal).",
      );
      // Only reset on error — on success the browser is navigating away.
      setPortalLoading(false);
    }
  };

  if (isPastDue) {
    return (
      <BannerShell
        tone="critical"
        lead="Échec de paiement."
        detail={`Votre dernier règlement a été refusé. ${managerContact ?? 'Mettez à jour votre moyen de paiement.'}${
          !managerContact && !canUpdatePayment ? " Prévenez l'administrateur de votre cabinet." : ''
        }`}
        action={
          canUpdatePayment
            ? {
                label: portalLoading ? 'Ouverture…' : 'Mettre à jour le paiement',
                onClick: handleUpdatePayment,
                disabled: portalLoading,
                primary: true,
              }
            : undefined
        }
      />
    );
  }

  // Abonnement mis en pause dans Stripe par l'équipe Hector : rien à régler ici, et
  // « Régler mon abonnement » mènerait à un onglet Facturation sans checkout (un
  // abonnement vivant existe déjà). Le bandeau le dit, sans bouton (décision Benoît 2026-10-09).
  if (status.billing_status === 'paused') {
    return (
      <BannerShell
        lead="Votre abonnement est actuellement en pause."
        detail={managerContact ?? 'Pour le reprendre, écrivez-nous à contact@hector.legal.'}
      />
    );
  }

  if (UNPAID_BANNER_SUSPENDED) return null;

  // Pas de bouton « masquer » : ce bandeau reste jusqu'au règlement (décision Benoît 2026-10-01).
  return (
    <BannerShell
      lead="Votre abonnement n'est pas encore réglé."
      detail={
        managerContact ??
        (status.is_admin
          ? 'Activez-le pour votre cabinet en quelques instants.'
          : "Contactez l'administrateur de votre cabinet.")
      }
      action={
        status.is_admin && !manager
          ? { label: 'Régler mon abonnement', onClick: () => navigate('/settings#facturation'), primary: true }
          : undefined
      }
    />
  );
}

interface BannerAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Filled pill — the one thing to do (settle, fix the payment). Otherwise a quiet outlined pill. */
  primary?: boolean;
}

/**
 * Habillage unique des quatre variantes (décision Benoît 2026-10-02 : sobre,
 * sans icône ni aplat de couleur). C'est une carte, pas une bande pleine largeur :
 * même filet, même rayon et même retrait de texte que la fenêtre de page posée
 * dessous, dont `DashboardLayout` lui donne aussi la gouttière — les deux se
 * lisent comme une seule colonne. Le fond est neutre ;
 * la hiérarchie tient à la typographie — une amorce en noir, le reste en gris —
 * et la seule couleur est le rouge de l'amorce d'un échec de paiement.
 * `tone="critical"` porte aussi `role="alert"` pour les lecteurs d'écran.
 */
function BannerShell({
  lead,
  detail,
  action,
  onDismiss,
  tone = 'neutral',
}: {
  lead: string;
  detail: string;
  action?: BannerAction;
  /** Present only on the variants that may be hidden for the session. */
  onDismiss?: () => void;
  tone?: 'neutral' | 'critical';
}) {
  return (
    <div
      role={tone === 'critical' ? 'alert' : 'status'}
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-grey-200 bg-white px-[22px] py-2"
    >
      <p className="min-w-0 flex-1 text-[13px] leading-5 tracking-[-0.01em] text-grey-500">
        <span className={cn('font-medium', tone === 'critical' ? 'text-red-600' : 'text-grey-900')}>{lead}</span>{' '}
        {detail}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          disabled={action.disabled}
          className={cn(
            'shrink-0 rounded-full px-3.5 py-1 text-[13px] font-medium transition-colors disabled:opacity-60',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            action.primary
              ? 'bg-charcoal text-white hover:bg-charcoal/90'
              : 'border border-black/10 bg-white text-grey-900 hover:bg-grey-50',
          )}
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Masquer ce message pour cette session"
          className="shrink-0 text-[13px] text-grey-400 transition-colors hover:text-grey-700"
        >
          Masquer
        </button>
      )}
    </div>
  );
}
